/**
 * Considerations
 * if path is greater we can ignore it
 * fallback path query params should not be considered
 */

export enum FAB_POSITION {
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
  CENTER_LEFT = 'CENTER_LEFT',
  CENTER_RIGHT = 'CENTER_RIGHT',
  POP_UP = 'POP_UP',
}

export interface IPageConfigProcessed {
  [key: string]: {
    widgetId: string;
    fabPosition: FAB_POSITION;
  }[];
}

interface IShowWidget {
  currentWidgetId: string;
  customUrls: IPageConfigProcessed;
  currentUrl: string;
}

interface IParsedUrl {
  path: string;
  query: string[];
  count: number;
  template?: string;
}

function isInIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

export class WidgetUtils {
  isValidUrl(urlString: string) {
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)?' +
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
        '((\\d{1,3}\\.){3}\\d{1,3}))' +
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
        '(\\?[;&a-z\\d%_.~+=-]*)?' +
        '(\\#[-a-z\\d_]*)?$',
      'i',
    );
    return !!urlPattern.test(urlString);
  }

  isHomePage(urlString: string) {
    const urlPattern = urlString.match(/^\/(\?.*)?$/);
    return !!urlPattern;
  }

  //entry point
  showWidget(config: IShowWidget, isAutomationTest?: boolean) {
    try {
      if (!isAutomationTest && isInIframe()) {
        return false;
      }
      let updatedCustomUrls: IPageConfigProcessed = {};
      Object.keys(config.customUrls).forEach(key => {
        if (this.isValidUrl(key)) {
          const newUrl = new URL(key);
          const correctKey = newUrl.href.replace(newUrl.origin, '');
          updatedCustomUrls[correctKey] = config.customUrls[key];
        } else {
          updatedCustomUrls[key] = config.customUrls[key];
        }
      });
      const updatedConfig: IShowWidget = {
        ...config,
        customUrls: updatedCustomUrls,
      };
      //handling home page alone
      const homePageWidgetsArray = updatedConfig.customUrls['/'] ?? [];
      if (homePageWidgetsArray.length) {
        const filtered = homePageWidgetsArray.filter(
          widget => widget.widgetId == config.currentWidgetId,
        );
        if (filtered.length) {
          if (updatedConfig.currentUrl == '/') {
            return true;
          }
          const constructedUrl = new URL(
            'https://bik.ai' + updatedConfig.currentUrl,
          );
          if (constructedUrl.pathname == '/') {
            return true;
          }
        }
      }

      //handling all pages
      const allPagesWidgetsArray = updatedConfig.customUrls['/*'] ?? [];
      if (allPagesWidgetsArray.length) {
        const filtered = allPagesWidgetsArray.filter(
          widget => widget.widgetId == config.currentWidgetId,
        );
        if (filtered.length) {
          const currentWidgetPosition = filtered[0].fabPosition;
          const allWidgetedUrls = Object.keys(updatedConfig.customUrls);
          const filteredWidgetedUrls = allWidgetedUrls.filter(
            url => url !== '/*',
          );
          const priorityUrls = [];
          for (let i = 0; i < filteredWidgetedUrls.length; i++) {
            if (updatedConfig.currentUrl.startsWith(filteredWidgetedUrls[i])) {
              priorityUrls.push(filteredWidgetedUrls[i]);
            }
          }
          if (!priorityUrls.length) {
            return true;
          }
          for (let i = 0; i < priorityUrls.length; i++) {
            const clashingWidgets = updatedConfig.customUrls[
              priorityUrls[i]
            ].filter(widget => {
              return (
                widget.widgetId !== updatedConfig.currentWidgetId &&
                widget.fabPosition === currentWidgetPosition
              );
            });
            if (clashingWidgets.length) {
              return false;
            }
          }
          return true;
        }
      }
      const ans = this.findExactMatch(updatedConfig);
      if (ans && ans === config.currentWidgetId) {
        return true;
      }
      const fuzzyAns = this.tryFuzzyMatch(updatedConfig);
      return fuzzyAns && fuzzyAns === config.currentWidgetId;
    } catch (err: any) {
      return false;
    }
  }

  findExactMatch(config: IShowWidget) {
    try {
      // try exact match
      let ans;
      if (config.customUrls[config.currentUrl]) {
        const values = config.customUrls[config.currentUrl];
        if (values.length) {
          values.forEach(item => {
            if (item.widgetId == config.currentWidgetId) {
              ans = config.currentWidgetId;
            }
          });
        }
      }
      return ans;
    } catch (err: any) {
      return;
    }
  }

  tryFuzzyMatch(config: IShowWidget) {
    try {
      let ans = null;
      const currentUrlRaw = config.currentUrl.replace(/\/\?/, '?');
      const currentUrl = this.separatePathAndQuery(
        currentUrlRaw,
        config.currentWidgetId,
      );
      if (!currentUrl) {
        return;
      }
      // try max path match
      const onlyUrls = Object.keys(config.customUrls);
      const currentUrlPathLength = currentUrl.path.split('/').length;
      const possibleMatches: IParsedUrl[] = [];
      let possibleFallback: IParsedUrl = {
        path: '',
        query: [],
        count: 0,
      };
      onlyUrls.forEach(url => {
        // if number of paths are greater than current url paths - ignore them
        // only consider the path part here - remove/ignore the query params
        if (this.isHomePage(url)) return;
        const values = config.customUrls[url];
        values.forEach(item => {
          const urlPath = this.separatePathAndQuery(url, item.widgetId);
          const urlPathLength = urlPath?.path.split('/').length;
          if (urlPathLength && urlPathLength > currentUrlPathLength) {
            // Do nothing
          } else if (urlPathLength == currentUrlPathLength) {
            // check if they match
            if (currentUrl?.path == urlPath?.path) {
              possibleMatches.push(urlPath);
            }
          } else {
            // fallback
            // consider only pure url i.e. only path and no query params
            if (!urlPath?.query.length) {
              if (
                currentUrl?.path.includes(urlPath?.path as string) &&
                currentUrl.count > possibleFallback.count
              ) {
                possibleFallback = urlPath as unknown as IParsedUrl;
              }
            } else {
              // Do nothing
            }
          }
        });
      });

      if (possibleMatches.length) {
        // if there are query params in currentUrl calculate max query match
        if (currentUrl && currentUrl.query.length) {
          // find max query match
          // even if one query param matches, consider that url
          // else fallback to exact path match
          let maxMatchPercentage = 0;

          const currentQueryHashMap = new Map();
          currentUrl.query.forEach(param => {
            currentQueryHashMap.set(param, 1);
          });
          possibleMatches.forEach(possibleMatch => {
            let matchCount = 0;
            possibleMatch.query.forEach((query: string) => {
              if (currentQueryHashMap.has(query)) {
                matchCount++;
              }
            });
            const matchPercentage = currentUrl
              ? (matchCount /
                  (possibleMatch.query.length + currentUrl.query.length)) *
                100
              : 0;
            if (maxMatchPercentage < matchPercentage) {
              maxMatchPercentage = matchPercentage;
              ans = possibleMatch;
            }
          });

          if (ans) {
            // Do nothing
          } else {
            ans = this.possibleMatchWithZeroQueryParam(possibleMatches);
            if (ans && ans.length) {
              ans = ans[0];
            } else {
              // if that is also empty, return best fallback
              ans = possibleFallback;
            }
          }
        } else {
          // if there are no query params in currentUrl, return the option with 0 query params
          ans = this.possibleMatchWithZeroQueryParam(possibleMatches);
          if (ans && ans.length) {
            ans = ans[0];
          } else {
            // if that is also empty, return best fallback
            ans = possibleFallback;
          }
        }
      } else {
        ans = possibleFallback;
      }
      return ans?.template || null;
    } catch (err: any) {
      return;
    }
  }

  separatePathAndQuery(url: string, widgetId: string) {
    try {
      // any dummy base url will do
      url = 'https://www.bik.ai' + url;
      const newUrl = new URL(url);
      const obj: IParsedUrl = {
        path: '',
        query: [],
        count: 0,
        template: widgetId,
      };
      obj.path = newUrl.pathname;
      obj.count = newUrl.pathname.split('/').length;
      if (newUrl.search) {
        const params = newUrl.search.split('?');
        obj.query = params[1].split('&');
      }
      return obj;
    } catch (err: any) {
      return;
    }
  }

  possibleMatchWithZeroQueryParam(possibleMatches: IParsedUrl[]) {
    try {
      return possibleMatches.filter(
        possibleMatch => !possibleMatch.query.length,
      );
    } catch (err: any) {
      return;
    }
  }
}
